import { tr } from '@/lib/i18n-core';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Baby,
  BedDouble,
  Blocks,
  BookOpen,
  CalendarCheck,
  CalendarHeart,
  ClipboardCheck,
  ClipboardList,
  Droplet,
  Droplets,
  FileCheck2,
  FlaskConical,
  HandHeart,
  HeartPulse,
  LayoutList,
  ListChecks,
  Lock,
  MessageSquareText,
  NotebookPen,
  NotebookText,
  PenLine,
  PersonStanding,
  Pill,
  RotateCcw,
  Ruler,
  ScanLine,
  ShieldPlus,
  Stethoscope,
  Syringe,
  Tablets,
  Utensils,
  UsersRound,
  Wind,
  type LucideIcon,
} from 'lucide-react';
import {
  EMR_FORM_LABEL_NAMESPACE,
  emrFormDefinition,
  type EmrContext,
  type EmrFormConfigResponse,
  type EmrFormConfigRow,
  type EmrFormGroup,
} from '@his/shared';
import { del, get, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { useWorkingBranchId } from '@/lib/branch';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormActions, FormStateScope } from '@/components/ui/form-screen';
import { Alert, Button, Field, Select, Skeleton, Switch, Tooltip } from '@/components/ui/primitives';
import { StatusBadge, type StatusTone } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toast';
import { DepartmentPhrasesCard } from './DepartmentPhrasesCard';
import {
  draftChanges,
  draftFrom,
  draftIsDirty,
  draftSummary,
  emrContextsFor,
  formState,
  groupsFor,
  rowsInGroup,
  withEnabled,
  withMove,
  withRequired,
  withReset,
  type EmrDraft,
  type EmrFormState,
} from './emr-forms-draft';

const FORM_ICONS: Record<string, LucideIcon> = {
  vitals: HeartPulse,
  complaint_history: MessageSquareText,
  review_of_systems: ListChecks,
  examination: Stethoscope,
  diagnosis: ClipboardCheck,
  plan_follow_up: CalendarCheck,
  prescription: Pill,
  progress_note: NotebookPen,
  lab_order: FlaskConical,
  radiology_order: ScanLine,
  procedure_order: Syringe,
  medication_order: Tablets,
  iv_fluid_order: Droplets,
  diet_order: Utensils,
  nursing_order: HandHeart,
  blood_transfusion_order: Droplet,
  oxygen_order: Wind,
  physiotherapy_order: PersonStanding,
  consult_order: UsersRound,
  vitals_monitoring_order: Activity,
  discharge_summary: FileCheck2,
  obstetric_history: Baby,
  menstrual_history: CalendarHeart,
  paediatric_history: Blocks,
  immunisation_status: ShieldPlus,
  growth_measurement: Ruler,
  procedure_note: NotebookText,
  patient_education: BookOpen,
  pre_procedure_consent: PenLine,
};

const GROUP_ICONS: Record<EmrFormGroup, LucideIcon> = {
  section: LayoutList,
  ipd_form: BedDouble,
  order: ClipboardList,
  clinical_form: NotebookText,
};

const STATE_TONE: Record<EmrFormState, StatusTone> = {
  required: 'success',
  optional: 'info',
  hidden: 'muted',
};

const STATE_LABEL: Record<EmrFormState, string> = {
  required: 'emr_forms_required',
  optional: 'emr_forms_state_optional',
  hidden: 'emr_forms_state_hidden',
};

interface DepartmentOption {
  id: string;
  name: string;
  kind?: string;
  isActive?: boolean;
  branchId?: string | null;
}

interface Scope {
  context: EmrContext;
  departmentId: string;
}

function formLabel(key: string): string {
  const definition = emrFormDefinition(key);
  return definition ? tr(EMR_FORM_LABEL_NAMESPACE, definition.labelKey) : key;
}

function configKey(scope: Scope) {
  return ['emr-forms', 'config', scope.context, scope.departmentId] as const;
}

export function EmrFormsTab() {
  const { can, canWrite, hasModule, user } = useAuth();
  const common = useTranslation('common');
  const toast = useToast();
  const queryClient = useQueryClient();
  const branchId = useWorkingBranchId();
  const branchName = (user?.branches ?? []).find((branch) => branch.id === branchId)?.name;
  const contexts = emrContextsFor(hasModule);
  const hospitalWide = Boolean(user?.isSuperAdmin || (user?.roles ?? []).some((role) => role.branchId === null));
  const mayEdit = canWrite && can('emr.config.manage', 'tenant.settings.manage');

  const [scope, setScope] = useState<Scope>({ context: contexts[0] ?? 'opd', departmentId: '' });
  const [pendingScope, setPendingScope] = useState<Scope | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [draft, setDraft] = useState<EmrDraft>({});
  const [baseline, setBaseline] = useState<EmrFormConfigRow[] | null>(null);

  const departments = useQuery({
    queryKey: ['departments', 'branch', branchId],
    queryFn: () => get<DepartmentOption[]>(`/org/departments${qs({ branchId: branchId || undefined })}`),
    enabled: can('department.read', 'department.manage'),
    select: (rows) => rows.filter((row) => row.isActive !== false && (row.kind ?? 'clinical') === 'clinical').sort((a, b) => a.name.localeCompare(b.name)),
  });

  const config = useQuery({
    queryKey: configKey(scope),
    queryFn: () => get<EmrFormConfigResponse>(`/emr-forms/config${qs({ context: scope.context, departmentId: scope.departmentId || undefined })}`),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const rows = config.data?.forms ?? null;
  if (rows && rows !== baseline) {
    setBaseline(rows);
    setDraft(draftFrom(rows));
  }
  const ready = Boolean(rows && rows === baseline);
  const dirty = ready && rows ? draftIsDirty(rows, draft) : false;
  const departmentScope = scope.departmentId !== '';
  const departmentName = departments.data?.find((row) => row.id === scope.departmentId)?.name ?? '';
  const scopeLocked = !departmentScope && !hospitalWide;
  const editable = mayEdit && !scopeLocked;

  const applyResponse = (response: EmrFormConfigResponse) => {
    queryClient.setQueryData(configKey(scope), response);
    void queryClient.invalidateQueries({ queryKey: ['emr-forms', 'config'], refetchType: 'none' });
  };

  const save = useMutation({
    mutationFn: () => {
      const changes = draftChanges(rows ?? [], draft);
      return put<EmrFormConfigResponse>('/emr-forms/config', {
        context: scope.context,
        departmentId: scope.departmentId || null,
        rules: changes.rules,
        reset: changes.reset,
      });
    },
    onSuccess: (response) => {
      applyResponse(response);
      toast.success(tr('admin', 'emr_forms_saved'));
    },
    onError: (caught) => toast.error(tr('admin', 'emr_forms_could_not_save'), describeError(caught)),
  });

  const resetScope = useMutation({
    mutationFn: () =>
      del<EmrFormConfigResponse>(`/emr-forms/config${qs({ context: scope.context, departmentId: scope.departmentId || undefined })}`),
    onSuccess: (response) => {
      applyResponse(response);
      toast.success(tr('admin', 'emr_forms_reset_done'));
    },
    onError: (caught) => toast.error(tr('admin', 'emr_forms_could_not_save'), describeError(caught)),
  });

  const requestScope = (next: Scope) => {
    if (next.context === scope.context && next.departmentId === scope.departmentId) return;
    if (dirty) setPendingScope(next);
    else setScope(next);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!dirty || save.isPending) return;
    save.mutate();
  };

  const summary = rows && ready ? draftSummary(rows, draft) : null;
  const anyOverride = Boolean(rows?.some((row) => row.overridden));

  return (
    <FormStateScope dirty={dirty}>
      <form onSubmit={submit} noValidate className="space-y-5">
        <section className="overflow-hidden rounded-xl border bg-card shadow-sm">
          <header className="flex items-start gap-3 border-b px-5 py-4">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
              <ClipboardList className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold leading-tight">{tr('admin', 'emr_forms')}</h2>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{tr('admin', 'emr_forms_description')}</p>
            </div>
          </header>
          <div className="grid gap-4 px-5 py-4 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-end">
            <Field label={tr('admin', 'emr_forms_visit_type')}>
              <div role="radiogroup" className="inline-flex w-full rounded-lg border border-border/70 bg-muted/60 p-1 sm:w-auto">
                {contexts.map((context) => {
                  const selected = scope.context === context;
                  return (
                    <button
                      key={context}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => requestScope({ context, departmentId: scope.departmentId })}
                      className={cn(
                        'flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-ring sm:flex-none',
                        selected ? 'bg-card text-foreground shadow-sm ring-1 ring-border/60' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {context === 'opd' ? <Stethoscope className="h-4 w-4" aria-hidden /> : <BedDouble className="h-4 w-4" aria-hidden />}
                      {tr('admin', context === 'opd' ? 'emr_forms_context_opd' : 'emr_forms_context_ipd')}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field
              label={tr('admin', 'emr_forms_department')}
              hint={branchName ? tr('admin', 'emr_forms_departments_of_branch', { branch: branchName }) : undefined}
            >
              <Select value={scope.departmentId} onChange={(event) => requestScope({ context: scope.context, departmentId: event.target.value })}>
                <option value="">{tr('admin', 'emr_forms_all_departments')}</option>
                {(departments.data ?? []).map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {summary && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/30 px-5 py-2.5 text-xs text-muted-foreground">
              <span className="tabular">{tr('admin', 'emr_forms_summary', summary)}</span>
              {editable && anyOverride && (
                <Button type="button" variant="ghost" size="xs" onClick={() => setConfirmReset(true)} disabled={resetScope.isPending}>
                  <RotateCcw aria-hidden />
                  {tr('admin', departmentScope ? 'emr_forms_reset_department' : 'emr_forms_reset_hospital')}
                </Button>
              )}
            </div>
          )}
        </section>

        {!mayEdit && <Alert>{tr('admin', 'you_can_view_these_settings_but_not_change_them')}</Alert>}
        {mayEdit && scopeLocked && <Alert variant="warning">{tr('admin', 'emr_forms_hospital_wide_needs_access')}</Alert>}
        {config.isError && <Alert variant="destructive">{`${tr('admin', 'emr_forms_could_not_load')}: ${describeError(config.error)}`}</Alert>}

        {scope.context === 'opd' && <DepartmentPhrasesCard departmentId={scope.departmentId} editable={mayEdit} />}

        {!ready && !config.isError && (
          <div className="space-y-3">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-40 w-full rounded-xl" />
            ))}
          </div>
        )}

        {ready &&
          rows &&
          groupsFor(scope.context).map((group) => {
            const members = rowsInGroup(rows, draft, group);
            if (members.length === 0) return null;
            const GroupIcon = GROUP_ICONS[group];
            const keys = members.map((row) => row.key);
            return (
              <section key={group} className="overflow-hidden rounded-xl border bg-card shadow-sm" aria-label={tr('admin', `emr_forms_group_${group}`)}>
                <header className="flex items-center gap-3 border-b px-5 py-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                    <GroupIcon className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0 leading-tight">
                    <h3 className="text-sm font-semibold">{tr('admin', `emr_forms_group_${group}`)}</h3>
                    <p className="mt-0.5 text-xs text-muted-foreground">{tr('admin', `emr_forms_group_${group}_hint`)}</p>
                  </div>
                </header>
                <ul className="divide-y">
                  {members.map((row, index) => (
                    <FormRow
                      key={row.key}
                      row={row}
                      draft={draft}
                      departmentScope={departmentScope}
                      editable={editable}
                      first={index === 0}
                      last={index === members.length - 1}
                      onEnabled={(enabled) => setDraft((current) => withEnabled(current, row.key, enabled))}
                      onRequired={(required) => setDraft((current) => withRequired(current, row.key, required))}
                      onMove={(delta) => setDraft((current) => withMove(current, keys, index, delta))}
                      onReset={() => setDraft((current) => withReset(current, row))}
                    />
                  ))}
                </ul>
              </section>
            );
          })}

        {editable && ready && (
          <FormActions
            submitLabel={tr('admin', 'emr_forms_save')}
            cancelLabel={tr('admin', 'emr_forms_discard')}
            loading={save.isPending}
            disabled={!dirty}
            onCancel={() => rows && setDraft(draftFrom(rows))}
          />
        )}
      </form>

      {pendingScope && (
        <ConfirmDialog
          title={common.t('form.unsaved_title')}
          description={common.t('form.unsaved_body')}
          confirmLabel={common.t('form.discard')}
          cancelLabel={common.t('form.keep_editing')}
          destructive
          onConfirm={() => {
            setScope(pendingScope);
            setPendingScope(null);
          }}
          onClose={() => setPendingScope(null)}
        />
      )}

      {confirmReset && (
        <ConfirmDialog
          title={
            departmentScope
              ? tr('admin', 'emr_forms_reset_department_title', { department: departmentName })
              : tr('admin', 'emr_forms_reset_hospital_title')
          }
          description={
            departmentScope
              ? tr('admin', 'emr_forms_reset_department_body', { department: departmentName })
              : tr('admin', 'emr_forms_reset_hospital_body')
          }
          confirmLabel={tr('admin', departmentScope ? 'emr_forms_reset_department' : 'emr_forms_reset_hospital')}
          destructive
          loading={resetScope.isPending}
          onConfirm={() => resetScope.mutateAsync()}
          onClose={() => setConfirmReset(false)}
        />
      )}
    </FormStateScope>
  );
}

function FormRow({
  row,
  draft,
  departmentScope,
  editable,
  first,
  last,
  onEnabled,
  onRequired,
  onMove,
  onReset,
}: {
  row: EmrFormConfigRow;
  draft: EmrDraft;
  departmentScope: boolean;
  editable: boolean;
  first: boolean;
  last: boolean;
  onEnabled: (enabled: boolean) => void;
  onRequired: (required: boolean) => void;
  onMove: (delta: -1 | 1) => void;
  onReset: () => void;
}) {
  const current = draft[row.key];
  if (!current) return null;
  const Icon = FORM_ICONS[row.key] ?? NotebookText;
  const name = formLabel(row.key);
  const state = formState(current);
  const locked = row.lockedRequired;
  const switchesDisabled = !editable || locked;
  const sourceLabel = current.own
    ? tr('admin', departmentScope ? 'emr_forms_overridden' : 'emr_forms_changed')
    : tr('admin', departmentScope ? 'emr_forms_inherits_all_departments' : 'emr_forms_standard');

  const switches = (
    <div className="flex items-center gap-4 rounded-md focus-ring" tabIndex={locked ? 0 : undefined}>
      <Switch checked={current.enabled} disabled={switchesDisabled} onCheckedChange={onEnabled} label={tr('admin', 'emr_forms_enabled')} />
      <Switch checked={current.required} disabled={switchesDisabled} onCheckedChange={onRequired} label={tr('admin', 'emr_forms_required')} />
    </div>
  );

  return (
    <li className={cn('flex flex-wrap items-center gap-x-4 gap-y-3 px-5 py-3 transition-colors', !current.enabled && 'bg-muted/30')}>
      <div className="flex min-w-0 flex-1 basis-56 items-center gap-3">
        <span
          className={cn(
            'grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors',
            current.enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('truncate text-sm font-medium', !current.enabled && 'text-muted-foreground')}>{name}</span>
            <StatusBadge status={state} tone={STATE_TONE[state]} label={tr('admin', STATE_LABEL[state])} />
            {locked && <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
          </div>
          <p className={cn('mt-0.5 text-xs', current.own ? 'font-medium text-warning' : 'text-muted-foreground')}>{sourceLabel}</p>
        </div>
      </div>
      <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end">
        {locked ? <Tooltip content={tr('admin', 'emr_forms_locked_hint')}>{switches}</Tooltip> : switches}
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!editable || first}
            aria-label={tr('admin', 'emr_forms_move_up', { name })}
            onClick={() => onMove(-1)}
          >
            <ArrowUp className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!editable || last}
            aria-label={tr('admin', 'emr_forms_move_down', { name })}
            onClick={() => onMove(1)}
          >
            <ArrowDown className="h-4 w-4" aria-hidden />
          </Button>
          <Tooltip content={current.own ? tr('admin', 'emr_forms_reset_name', { name }) : null}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn('h-8 w-8', !current.own && 'invisible')}
              disabled={!editable || !current.own}
              aria-label={tr('admin', 'emr_forms_reset_name', { name })}
              onClick={onReset}
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
            </Button>
          </Tooltip>
        </div>
      </div>
    </li>
  );
}
